import { string, ref, bool } from "yup";
import { differenceInYears, getDaysInMonth } from "date-fns";

import {
  MIN_PASSWORD_LENGTH,
  MAX_PASSWORD_LENGTH,
  MAX_EMAIL_LOCAL_PART_LENGTH,
  MAX_EMAIL_DOMAIN_PART_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_USERNAME,
  MAX_OTP_LENGTH,
  MAX_NAME_LENGTH,
  MAX_SUPPORT_MESSAGE_LENGTH,
} from "./lengthConstants";
import {
  emailRegExp,
  InstagramUsernamePatter,
  NoSpaceNamePattern,
  NoSpacePattern,
  PasswordPattern,
  UsernameAllowedChars,
} from "./patterns";
import { validateOnlySpaces } from "@helpers/validation";

export const TermsOfUseSchema = bool().isTrue("TERMS_OF_USE");

export const AdultsPrivacySchema = bool().isTrue("ADULTS_POLICY");

export const EmailSchema = string()
  .required("EMAIL_REQUIRED")
  .email("EMAIL_INVALID")
  .max(MAX_EMAIL_LENGTH, "EMAIL_INVALID")
  .trim()
  .matches(emailRegExp, "EMAIL_INVALID")
  .test("emailformat", "EMAIL_INVALID", (value) => {
    if (!value) {
      return false;
    }

    const [localPart, domainPart] = value.split("@");

    return (
      localPart?.length <= MAX_EMAIL_LOCAL_PART_LENGTH &&
      domainPart?.length <= MAX_EMAIL_DOMAIN_PART_LENGTH
    );
  });

export const PasswordSchema = string()
  .min(MIN_PASSWORD_LENGTH, "MIN")
  .max(MAX_PASSWORD_LENGTH, "MAX")
  .matches(PasswordPattern, "PWD_PATTERN")
  .matches(NoSpacePattern, "NO_SPACES")
  .required("REQUIRED");

export const SignInPasswordSchema = string().required("REQUIRED");

export const ConfirmPasswordSchema = string()
  .oneOf([ref("password"), null], "DOES_NOT_MATCH_PWD")
  .required("REQUIRED");

export const UsernameSchema = string()
  .trim()
  .matches(UsernameAllowedChars, "LETTERS_DIGITS_PERIOD_UNDERSCORE_ALLOWED")
  .matches(NoSpacePattern, "LETTERS_DIGITS_PERIOD_UNDERSCORE_ALLOWED")
  .matches(InstagramUsernamePatter, "NO_FIRST_LAST_PERIOD")
  .test("NO_START_END_PERIOD", "NO_FIRST_LAST_PERIOD", (value) => {
    return !(value?.startsWith(".") || value?.endsWith("."));
  })
  .test("NOT_ONLY_UNDERSCORE", "NOT_ONLY_UNDERSCORE", (value) => {
    return !(value?.length && !value?.replaceAll("_", "")?.length);
  })
  .max(MAX_USERNAME, "ENTER_USERNAME_UNDER")
  .required("REQUIRED");

export const TwoFactorTokenSchema = string()
  .min(MAX_OTP_LENGTH, "INVALID_OTP")
  .max(MAX_OTP_LENGTH, "INVALID_OTP")
  .required("INVALID_OTP");

export const BiographySchema = string().test(
  "NOT_SPACE_ONLY",
  "SPACES_ONLY_NOT_ALLOWED",
  validateOnlySpaces,
);

export const DateOfBirthSchema = string()
  .test(
    "validDate",
    "INVALID_DATE",
    (_value, { parent: { day, month, year } }) => {
      if (!day && !month && !year) {
        return true;
      }

      const daysInMonth = getDaysInMonth(new Date(year, month - 1));

      return daysInMonth >= day;
    },
  )
  .test("tooYoung", "MIN_AGE", (_value, { parent: { day, month, year } }) => {
    if (!year || !month || !day) {
      return true;
    }

    return differenceInYears(new Date(), new Date(year, month - 1, day)) >= 18;
  })
  .test("tooOld", "MAX_AGE", (_value, { parent: { day, month, year } }) => {
    if (!year || !month || !day) {
      return true;
    }

    return differenceInYears(new Date(), new Date(year, month - 1, day)) <= 100;
  });

export const AddressSchema = string()
  .test("NOT_SPACE_ONLY", "SPACES_ONLY_NOT_ALLOWED", validateOnlySpaces)
  .min(2, "ADDRESS_MIN")
  .max(MAX_NAME_LENGTH, "ENTER_UNDER");

export const MessageSchema = string()
  .test("NOT_SPACE_ONLY", "SPACES_ONLY_NOT_ALLOWED", validateOnlySpaces)
  .max(MAX_SUPPORT_MESSAGE_LENGTH, "ENTER_UNDER")
  .required("REQUIRED");

export const NameSchema = string()
  .trim()
  .test("NOT_SPACE_ONLY", "SPACES_ONLY_NOT_ALLOWED", validateOnlySpaces)
  .matches(NoSpaceNamePattern, "LETTERS_DIGITS_PERIOD_UNDERSCORE_ALLOWED")
  .max(MAX_NAME_LENGTH, "ENTER_UNDER");
